import React, { forwardRef, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { uniqBy, isEmpty, split, filter } from 'lodash';
import { FieldTypes, generateFormFields, parseSelectOptions } from 'utils/formUtils';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { Form } from 'antd';

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  type: 'applicationSecurity',
  noNameProjectList: [{ hosts: undefined }]
};

function NonameMappingForm({disableProjectFields = false, ...props}, ref) {
  const [form] = Form.useForm();
  const [hostProjects, setHostProjects] = useState([]);
  const [dastProjects, setDastProjects] = useState([]);
  const [mappedProjectList] = useState(() => {
    // this state contains the already mapped host
    // This is stored because the project are paginated
    // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
    if (props.record) {
      const { hosts, hostName, applicationIds, applicationNames } = props.record;
      const refArray = isEmpty(applicationIds) ? split(hosts, ',') : split(applicationIds, ',');
      const refNameArray = isEmpty(applicationNames) ? split(hostName, ',') : split(applicationNames, ',');
      return { projectId: refArray.map((id, index) => ({id, name: refNameArray[index], otherProperties: isEmpty(applicationIds) ? null : { type: 'dast' }}))}
    }
    return { projectId: [] }
  })
  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [
      {
        ...initialFormValues,
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
      },
    ];
  });

  const optionsRef = useRef(mappedProjectList.projectId);

  const fetchRepos = useCallback(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });

    promise.then((data) => {
      if(mappedProjectList["projectId"].length > 0 && !isEmpty(data.projects)){
          data.projects = uniqBy([...data.projects, ...mappedProjectList["projectId"]], 'id');
      }
      const dastItems = filter(data.projects, item => item.otherProperties && item.otherProperties.type === 'dast');
      const otherItems = filter(data.projects, item => !item.otherProperties || item.otherProperties.type !== 'dast');
      
      setDastProjects(dastItems);
      setHostProjects(otherItems);
      optionsRef.current = uniqBy([...optionsRef.current, ...data.projects], 'id');
    }).catch(() => {
      message.error('Problem fetching hosts');
    })
        
    return cancel
  }, [props.loginConfigId,props.toolName, mappedProjectList]);

  useEffect(fetchRepos,[fetchRepos])


  const getExtraFieldsData = (config) => {
    const { hosts, applicationIds, type } = config;
    const refList = type === 'dast' ? applicationIds  : hosts;
    const data = optionsRef.current.reduce((acc, cv) => {
      const { id, name } = cv;
      if(refList.includes(id)){
        acc.idList.push(id);
        acc.nameList.push(name);
      }
      return acc;
    },{ idList: [], nameList: [] })
 
    return ({
      type,
      ...(type === 'dast' ? { applicationIds: data.idList.join(','), applicationNames: data.nameList.join(',') }: { hosts: data.idList.join(','), hostName: data.nameList.join(',')}),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    return ({
      type: config.type,
      ...(config.type === 'dast' ? { applicationIds: config.applicationIds.split(',')}: { hosts: config.hosts.split(',') }),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }, []);
  
  const projectListRender = useCallback((listField) => {

    const hostFields = [
      {
        name: [listField.name, "hosts"],
        colSpan: '12',
        label: 'Hosts',
        type: FieldTypes.MULTI_SELECT,
        disabled: disableProjectFields,
        required: true,
        options: parseSelectOptions(hostProjects),
        props: {
          autoFocus: !disableProjectFields
        }
      }
    ]

    const applicationFields = [
      {
        name: [listField.name, "applicationIds"],
        colSpan: '12',
        label: 'Application Name',
        type: FieldTypes.MULTI_SELECT,
        disabled: disableProjectFields,
        required: true,
        options: parseSelectOptions(dastProjects),
        props: {
          autoFocus: !disableProjectFields
        }
      }
    ]
    const selectedType = form.getFieldsValue().configurations[listField.name]?.type;
  
    return (
      <div className='m-b-sm'>
        {
          selectedType === 'dast'
          ?
          generateFormFields(applicationFields)
          :
          generateFormFields(hostFields)
        }
      </div>
    )
  }, [disableProjectFields,hostProjects, dastProjects, form]);

  const extraFields = useMemo(() => ([
      {
          name: "type",
          type: FieldTypes.RADIO,
          options: [{ label: 'Application Security', value: 'applicationSecurity' }, { label: 'DAST', value: 'dast' }],
          disabled: disableProjectFields
      },
      {
        name: (listField) => [listField.name, "noNameProjectList"],
        render: projectListRender
      }
  ]), [projectListRender, disableProjectFields]);

  return (
    <GenericMappingForm
      {...props}
      form={form}
      ref={ref}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
    />
  )
}

export default forwardRef(NonameMappingForm)
